///
/// EarthBrowser
///
/// Created by Matt Giger
/// Copyright (c) 2025 Geodata Labs
///

import {
	ContinuousGestureRecognizer,
	GestureState,
	type GestureEventData,
	type GestureRecognizerConfig,
	type TouchPoint
} from "./GestureRecognizer"

export interface LongPressGestureEventData extends GestureEventData {
	// Duration of the press so far (ms)
	duration: number
	// Number of fingers used
	touchCount: number
}

export interface LongPressGestureConfig extends GestureRecognizerConfig {
	// Minimum duration before press is recognized (in ms)
	minimumPressDuration?: number
	// Maximum movement allowed during press (in pixels)
	maxMovement?: number
	// Allow movement after recognition (drag while holding)
	allowMovementAfterRecognition?: boolean
}

/**
 * Recognizes long press (press and hold) gestures
 *
 * This is a continuous gesture - it begins when the minimum duration
 * is reached, and ends when the touch is released.
 */
export class LongPressGestureRecognizer extends ContinuousGestureRecognizer<LongPressGestureEventData> {
	private minimumPressDuration: number
	private maxMovement: number
	private allowMovementAfterRecognition: boolean

	private touchStartTime: number = 0
	private startLocation?: { x: number, y: number }
	private recognitionTimer?: ReturnType<typeof setTimeout>

	constructor(config: LongPressGestureConfig = {}) {
		super(config)
		this.minimumPressDuration = config.minimumPressDuration ?? 500
		this.maxMovement = config.maxMovement ?? 10
		this.allowMovementAfterRecognition = config.allowMovementAfterRecognition ?? true
	}

	get gestureType(): string {
		return "longpress"
	}

	handleTouchStart(touches: TouchPoint[]): void {
		if (!this._enabled) return

		// Update tracked touches
		touches.forEach(t => this.touches.set(t.id, t))

		// Check touch count requirements
		if (this.touchCount < this._minimumTouches || this.touchCount > this._maximumTouches) {
			this.clearTimer()
			this.fail()
			return
		}

		const centroid = this.getCentroid()
		this.touchStartTime = performance.now()
		this.startLocation = centroid

		// Start recognition timer
		this.recognitionTimer = setTimeout(() => {
			this.recognitionTimer = undefined
			const data = this.createEventData(GestureState.Began)
			this.begin(data)
		}, this.minimumPressDuration)
	}

	handleTouchMove(touches: TouchPoint[]): void {
		if (!this._enabled) return

		// Update tracked touches
		touches.forEach(t => {
			const existing = this.touches.get(t.id)
			if (existing) {
				existing.x = t.x
				existing.y = t.y
			}
		})

		const centroid = this.getCentroid()

		// Before recognition: fail if moved too much
		if (!this.isActive() && this.startLocation) {
			const dx = centroid.x - this.startLocation.x
			const dy = centroid.y - this.startLocation.y
			const distance = Math.sqrt(dx * dx + dy * dy)

			if (distance > this.maxMovement) {
				this.clearTimer()
				this.fail()
				return
			}
		}

		// After recognition: emit change events if movement allowed
		if (this.isActive()) {
			if (this.allowMovementAfterRecognition) {
				const data = this.createEventData(GestureState.Changed)
				this.change(data)
			} else if (this.startLocation) {
				const dx = centroid.x - this.startLocation.x
				const dy = centroid.y - this.startLocation.y
				const distance = Math.sqrt(dx * dx + dy * dy)

				if (distance > this.maxMovement) {
					this.cancel()
				}
			}
		}
	}

	handleTouchEnd(touches: TouchPoint[]): void {
		if (!this._enabled) return

		// Remove ended touches
		touches.forEach(t => this.touches.delete(t.id))

		// Only process when all touches are released
		if (this.touchCount > 0) return

		this.clearTimer()

		if (this.isActive()) {
			const data = this.createEventData(GestureState.Ended)
			this.end(data)
		} else if (!this.isTerminal()) {
			// Not yet recognized - fail
			this.fail()
		}
	}

	handleTouchCancel(): void {
		this.clearTimer()
		this.cancel()
	}

	private clearTimer(): void {
		if (this.recognitionTimer) {
			clearTimeout(this.recognitionTimer)
			this.recognitionTimer = undefined
		}
	}

	protected createEventData(state: GestureState): LongPressGestureEventData {
		const centroid = this.getCentroid()
		const location = this.touchCount > 0 ? centroid : (this.startLocation ?? { x: 0, y: 0 })

		return {
			type: "longPress",
			state,
			timestamp: performance.now(),
			x: location.x,
			y: location.y,
			duration: performance.now() - this.touchStartTime,
			touchCount: this._minimumTouches,
			ownerHits: this.getOwnerHits()
		}
	}

	override reset(): void {
		this.clearTimer()
		super.reset()
		this.startLocation = undefined
		this.touchStartTime = 0
	}
}

